import React, { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  Snackbar,
} from "@mui/material";
import pluralize from "pluralize";
import { RiCheckboxCircleLine, RiCloseLine } from "@remixicon/react";
import { useTheme } from "@mui/material/styles";

import { DeliveryPayloadT, DeliveryItemT } from "../../../types/ScheduleForm";
import useGaneshApi from "../../../hooks/useGaneshApi";
import { AuthContextType, useAuth } from "../../../contexts/AuthContext";

import { ScheduleDialogProps } from "../../../types/ScheduleForm";
import DeliveriesCombo from "./DeliveriesCombo";
import { generateFormLabelTypographyProps } from "./FormLabelProps";
import { formatDateYYYYMMDD } from "utils/formatting";

export default function DeliveriesDialog({
  onClose,
  selectedRows = [],
  action = "ADD",
  onSuccessCallback,
  onErrorCallback,
}: ScheduleDialogProps) {
  const userAuth: AuthContextType = useAuth();
  const { apiRequest } = useGaneshApi();
  const navigate = useNavigate();
  const { date_id } = useParams<{ date_id: string }>();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const formLabelTypographyProps = generateFormLabelTypographyProps(theme);
  const date =
    date_id && date_id.split("-").length === 3
      ? formatDateYYYYMMDD(new Date(date_id))
      : formatDateYYYYMMDD(new Date());
  const isChanging = action === "CHANGE";

  const [selectedDate, setSelectedDate] = useState(date);

  const [selectedItems, setSelectedItems] =
    useState<DeliveryItemT[]>(selectedRows);

  const initialVolumeUpgrade =
    isChanging && selectedRows.length === 1
      ? !!selectedRows[0].volume_upgrade_scheduled
      : true;

  const [volumeUpgrade, setVolumeUpgrade] =
    useState<boolean>(initialVolumeUpgrade);

  const [addAnother, setAddAnother] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarPath, setSnackbarPath] = useState("");
  const [isAdHoc, setIsAdHoc] = useState(false);
  const [adHocTicketId, setAdHocTicketId] = useState("");

  const postEndpoint = isChanging
    ? "/schedule/batch-update-deliveries"
    : "/schedule/batch-add-deliveries";

  const { mutate: postDelivery, isPending: isAddingDeliveries } = useMutation({
    mutationFn: async (payload: DeliveryPayloadT) => {
      const response = await apiRequest(postEndpoint, {
        method: "POST",
        body: payload,
      });
      return response as any;
    },
    onSuccess: (data: any, variables: DeliveryPayloadT) => {
      queryClient.invalidateQueries({
        queryKey: ["schedule", date_id],
      });

      if (isChanging) {
        onSuccessCallback?.(data);
        onClose();
      } else {
        if (action === "ADD_ONE") {
          onClose();
          onSuccessCallback?.(data);
          return;
        }

        const shippingPath = `/schedule/date/${selectedDate}`;
        if (addAnother) {
          const message = `${selectedItems.length} ${pluralize(
            "delivery",
            selectedItems.length
          )} scheduled for ${selectedDate}.`;

          setSnackbarOpen(true);
          setSnackbarMessage(message);
          setSnackbarPath(shippingPath);
          setSelectedItems([]);
        } else {
          onClose();
          onSuccessCallback?.(data);
          if (window.location.pathname !== shippingPath) {
            navigate(shippingPath, { replace: true });
          }
        }
      }
    },
    onError: (error: any) => {
      console.error("Delivery operation failed:", error);
      onErrorCallback?.(error);
    },
  });

  const isFormValid = selectedDate && selectedItems.length > 0;

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  const handleSubmit = () => {
    const formattedDate = formatDateYYYYMMDD(new Date(selectedDate));
    const payload: DeliveryPayloadT = {
      admin_id: userAuth.authAttributes?.id ?? null,
      deliveries: selectedItems.map((item) => ({
        delivery_id: item.delivery_id,
        target_fulfillment_date: formattedDate,
        schedule_volume_upgrade: volumeUpgrade,
        ...(isAdHoc
          ? {
              is_ad_hoc: true,
              ad_hoc_ticket_id: adHocTicketId,
            }
          : {}),
        ...(isChanging
          ? {
              delivery_schedule_id: item.delivery_fulfillment_id,
            }
          : {}),
      })),
    };
    postDelivery(payload);
  };

  const renderSubmitButtonLabel = () => {
    if (isChanging) {
      if (isAddingDeliveries) {
        return "Changing";
      }
      return "Change";
    }

    if (isAddingDeliveries) {
      return "Adding";
    }
    return "Add";
  };

  return (
    <>
      <DialogTitle>
        {isChanging ? "Change" : "Add"}
        {` ${pluralize("delivery", selectedItems.length)}`}
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <RiCloseLine />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "24px",
            alignItems: "start",
            width: "100%",
            margin: "0 auto",
          }}
        >
          <FormLabel htmlFor="date" {...formLabelTypographyProps}>
            Fulfillment Date *
          </FormLabel>
          <TextField
            id="date"
            type="date"
            size="small"
            fullWidth
            value={selectedDate}
            onChange={handleDateChange}
            inputProps={{
              min: formatDateYYYYMMDD(new Date()),
            }}
          />
          <FormLabel component="legend" {...formLabelTypographyProps}>
            Deliveries *
          </FormLabel>
          <DeliveriesCombo
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
            action={action}
          />
          <FormLabel
            component="legend"
            {...formLabelTypographyProps}
            sx={{ ...formLabelTypographyProps.sx, mt: 1 }}
          >
            Volume Upgrade
          </FormLabel>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="volume-upgrades"
              name="volume_upgrade"
              value={volumeUpgrade.toString()}
              onChange={(e) => setVolumeUpgrade(e.target.value === "true")}
            >
              <FormControlLabel
                value="true"
                control={<Radio size="small" />}
                label="Yes"
              />
              <FormControlLabel
                value="false"
                control={<Radio size="small" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
          <Box style={{ gridColumn: "2 / -1" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isAdHoc}
                  onChange={(e) => {
                    setIsAdHoc(e.target.checked);
                    if (!e.target.checked) setAdHocTicketId("");
                  }}
                  name="isAdHoc"
                  size="small"
                />
              }
              label="Is Ad Hoc?"
            />
            <TextField
              id="ad_hoc_ticket_id"
              label="Optional Ticket ID"
              size="small"
              sx={{ marginTop: 2 }}
              fullWidth
              value={adHocTicketId}
              disabled={!isAdHoc}
              onChange={(e) => {
                setAdHocTicketId(e.target.value);
              }}
            />
          </Box>
          <DialogActions
            style={{
              gridColumn: "1 / -1",
              justifyContent: "end",
              marginTop: "16px",
            }}
          >
            {!isChanging && (
              <FormControlLabel
                control={
                  <Switch
                    checked={addAnother}
                    onChange={(e) => setAddAnother(e.target.checked)}
                    name="addAnother"
                    size="small"
                  />
                }
                label="Create more"
              />
            )}
            <Button
              variant="contained"
              color={"success"}
              onClick={handleSubmit}
              sx={{ minWidth: "120px" }}
              disabled={!isFormValid || isAddingDeliveries}
            >
              {renderSubmitButtonLabel()}{" "}
              {pluralize("delivery", selectedItems.length)}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </>
  );
}
